import React from "react"
import { useQuery } from "@tanstack/react-query"
import { AlertCircle, RefreshCw } from "lucide-react"
import api from "../services/api"
import { useDashboardFilters } from "../state/dashboardFilters"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Skeleton } from "../components/ui/skeleton"
import DownloadsChart from "../components/AppStore/DownloadsChart"
import RevenueCard from "../components/AppStore/RevenueCard"
import ReviewsTable from "../components/AppStore/ReviewsTable"
import RatingSummary from "../components/AppStore/RatingSummary"
import StatCard from "../components/AppStore/StatCard"

export default function AppStorePage() {
  const { autoRefresh } = useDashboardFilters()

  const { data: summary, isLoading: summaryLoading, error: summaryError, refetch } = useQuery({
    queryKey: ["appstore-summary"],
    queryFn: () => api.getAppStoreSummary(),
    refetchInterval: autoRefresh ? 60000 : false, // Refresh every minute if auto-refresh is on
    retry: 1,
  })

  // Check if App Store Connect is configured
  const isConfigured = summary?.configured !== false

  if (summaryError || (summary && !isConfigured)) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">App Store Analytics</h1>
          <p className="text-muted-foreground">
            Monitor downloads, revenue, and reviews from App Store Connect.
          </p>
        </div>

        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-600">
              <AlertCircle className="h-5 w-5" />
              App Store Connect Not Configured
            </CardTitle>
            <CardDescription>
              To enable App Store analytics, you need to configure your App Store Connect API credentials.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Add the following environment variables to <code className="bg-muted px-1 py-0.5 rounded">/home/appuser/.analytics-env</code>:
            </p>
            <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto">
{`ASC_ISSUER_ID=your-issuer-id
ASC_KEY_ID=your-key-id
ASC_PRIVATE_KEY="-----BEGIN PRIVATE KEY-----
...your private key...
-----END PRIVATE KEY-----"
ASC_VENDOR_NUMBER=your-vendor-number
ASC_APP_ID=your-app-id`}
            </pre>
            <p className="text-sm text-muted-foreground">
              You can find these values in App Store Connect under Users and Access → Keys.
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Check Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">App Store Analytics</h1>
          <p className="text-muted-foreground">
            Monitor downloads, revenue, and reviews from App Store Connect.
          </p>
        </div>
        {summary?.lastUpdated && (
          <p className="text-xs text-muted-foreground">
            Last updated: {new Date(summary.lastUpdated).toLocaleTimeString()}
          </p>
        )}
      </div>

      {/* Summary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {summaryLoading ? (
          <>
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </>
        ) : (
          <>
            <StatCard
              title="Downloads Today"
              value={summary?.downloads?.today || 0}
              subtitle={`${summary?.downloads?.week || 0} this week`}
              trend={summary?.downloads?.week > 0 ? "up" : "neutral"}
            />
            <StatCard
              title="Revenue Today"
              value={`$${(summary?.revenue?.today || 0).toFixed(2)}`}
              subtitle={`$${(summary?.revenue?.week || 0).toFixed(2)} this week`}
              trend={summary?.revenue?.week > 0 ? "up" : "neutral"}
            />
            <StatCard
              title="Average Rating"
              value={summary?.reviews?.averageRating || "—"}
              subtitle={`${summary?.reviews?.totalCount || 0} reviews`}
              icon="star"
            />
            <StatCard
              title="Needs Response"
              value={summary?.reviews?.unrepliedCount || 0}
              subtitle="reviews awaiting reply"
              trend={summary?.reviews?.unrepliedCount > 0 ? "attention" : "neutral"}
            />
          </>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Downloads Chart */}
        <DownloadsChart />
        
        {/* Revenue Card */}
        <RevenueCard />
      </div>

      {/* Reviews Section */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Rating Summary */}
        <RatingSummary />
        
        {/* Reviews Table (spans 2 columns on xl) */}
        <div className="xl:col-span-2">
          <ReviewsTable />
        </div>
      </div>
    </div>
  )
}
